/* eslint-disable import/prefer-default-export */
/** @jsxImportSource @emotion/react */
import { useEffect, useState } from 'react';
import { useTheme } from '@emotion/react';
import { useSelector } from 'react-redux';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Popover from '@mui/material/Popover';
import useMatchConfidence from '../../Hooks/useMatchConfidence/useMatchConfidence';
import MatchConfidenceLevel from '../../Data/MatchConfidenceLevel';
import makeSmartMatchMeterStyling from './makeSmartMatchMeterStyling';
import SmartMatchMeterIcon from './SmartMatchMeterIcon';
import SmartMatchMeterInfoIcon from './SmartMatchMeterInfoIcon';

const getConfidenceDisplayText = (confidenceLevel) => {
  switch (confidenceLevel) {
    case MatchConfidenceLevel.FAIR:
      return 'Fair';
    case MatchConfidenceLevel.MEDIUM:
      return 'Medium';
    case MatchConfidenceLevel.HIGH:
      return 'High';
    case MatchConfidenceLevel.NULL:
    default:
      return '-';
  }
};

export const SmartMatchMeter = () => {
  const theme = useTheme();
  const { isShown, confidenceLevel } = useMatchConfidence();
  const matchingSupplierCount = useSelector(
    (store) => store.supplierMatches.matchingSupplierCount
  );
  const supplierMatchCountUp = useSelector(
    (store) => store.features.supplierMatchCountUp
  );
  const exitPhase = useSelector((store) => store.transition.exitPhase);
  const enterPhase = useSelector((store) => store.transition.enterPhase);
  const currentPage = useSelector((store) => store.navigation.currentPage);
  const [anchorEl, setAnchorEl] = useState(null);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [hasUserNavigated, setHasUserNavigated] = useState(false);

  useEffect(() => {
    if (exitPhase === false) {
      // On completion of the first half of a page change this boolean changes from true to false
      if (currentPage > 0 && hasUserNavigated === false) {
        setIsVisible(true); // Mount the component just before the next page has entered into the viewport (i.e. prior to the ENTER phase)
      }
    }
  }, [exitPhase]);

  useEffect(() => {
    if (enterPhase === false) {
      // On completion of a full page change this boolean changes from true to false
      if (currentPage > 0 && hasUserNavigated === false) {
        setHasUserNavigated(true); // The user has completed their first page change
      }
    }
  }, [enterPhase]);

  useEffect(() => {
    if (
      hasUserNavigated === true &&
      isVisible === true &&
      confidenceLevel !== '-'
    ) {
      const fadeInTimer = setTimeout(() => {
        const smartMatchMeterContainer = document.querySelector(
          "[data-testid='smartMatchMeter']"
        );
        // The container is absent when the meter is hidden or has already been
        // removed by a further page change, so there is nothing to fade in
        if (!smartMatchMeterContainer) return;

        // Kick off fade-in animation of the smartMatchMeter container after the page change has completed
        smartMatchMeterContainer.style.opacity = 1;
      }, 0);

      return () => clearTimeout(fadeInTimer);
    }

    return undefined;
  }, [hasUserNavigated]);

  const styling = makeSmartMatchMeterStyling({
    theme,
    confidenceLevel,
  });

  const handleOpen = () => {
    setIsPopoverOpen(true);
  };

  const handleClose = () => {
    setIsPopoverOpen(false);
  };

  if (!isShown) {
    return null;
  }

  // Show dash when count is null/undefined, OR when counting up and count is 0
  const isCountingUpWithZero =
    supplierMatchCountUp && matchingSupplierCount === 0;
  const displayCount =
    matchingSupplierCount == null || isCountingUpWithZero
      ? '-'
      : matchingSupplierCount;
  const confidenceDisplayText = getConfidenceDisplayText(confidenceLevel);

  return !isVisible ? null : (
    <Box css={styling.container} data-testid="smartMatchMeter">
      <Box css={styling.card} ref={setAnchorEl}>
        <Box css={styling.iconContainer}>
          <SmartMatchMeterIcon data-testid="smartMatchMeterIcon" />
        </Box>

        <Typography css={styling.label} data-testid="smartMatchMeterLabel">
          Smart Match Meter
        </Typography>

        <Box css={styling.statsContainer}>
          <Box css={styling.statItem}>
            <Typography css={styling.statLabel}>Matches</Typography>
            <Typography
              css={styling.matchesValue}
              data-testid="smartMatchMeterCount"
            >
              {displayCount}
            </Typography>
          </Box>

          <Box css={styling.statItem}>
            <Typography css={styling.statLabel}>Confidence</Typography>
            <Box css={styling.confidenceContainer}>
              <Box css={styling.confidenceBadge}>
                <Box css={styling.confidenceDot} />
                <Typography
                  css={styling.confidenceText}
                  data-testid="smartMatchMeterConfidenceText"
                >
                  {confidenceDisplayText}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>

        <Box
          css={styling.infoContainer}
          onClick={handleOpen}
          onMouseEnter={handleOpen}
          onMouseLeave={handleClose}
        >
          <SmartMatchMeterInfoIcon data-testid="smartMatchMeterInfoIcon" />
        </Box>
      </Box>

      <Popover
        data-testid="smartMatchMeterPopover"
        open={isPopoverOpen}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        disableRestoreFocus
        sx={{ pointerEvents: 'none' }}
        slotProps={{
          paper: {
            sx: {
              ...styling.popoverPaper,
              pointerEvents: 'auto',
            },
            onMouseEnter: handleOpen,
            onMouseLeave: handleClose,
          },
        }}
      >
        <Box css={styling.popoverArrow} />
        <Typography css={styling.popoverContent}>
          We&apos;re calculating your matches based on your unique requirements.
          The more we know, the higher our confidence to provide the right
          matches for you.
        </Typography>
      </Popover>
    </Box>
  );
};
